import { Provider } from 'cerebral';
import { isObservable, isObservableMap } from 'mobx';
import { ComputedClass } from './Computed';
var mutationMethods = [
    'concat',
    'pop',
    'push',
    'shift',
    'splice',
    'unshift',
];
function cleanPath(state, key, path) {
    var pathCopy = key ? path.concat(key) : path.slice();
    var isValid = false;
    while (!isValid) {
        var value = pathCopy.reduce(function (currentState, pathKey) {
            if (currentState === undefined) {
                return;
            }
            if (isObservableMap(currentState)) {
                return currentState.get(pathKey.toString());
            }
            if (typeof currentState === 'object' && pathKey in currentState) {
                return currentState[pathKey];
            }
        }, state);
        if (value === undefined) {
            pathCopy.splice(pathCopy.length - 2, 1);
        }
        else {
            isValid = true;
        }
    }
    return pathCopy;
}
function createValidator(state, execution, functionDetails, props, devtools, basePath) {
    if (basePath === void 0) { basePath = []; }
    var path = basePath;
    var validator = {
        get: function (target, key) {
            if (key === '$mobx') {
                return target[key];
            }
            if (isObservableMap(target)) {
                switch (key) {
                    case 'size':
                        path = cleanPath(state, null, path);
                        return target[key];
                    case 'forEach':
                        return function (cb) {
                            return target.keys().forEach(function (forEachKey) {
                                var args = [];
                                for (var _i = 1; _i < arguments.length; _i++) {
                                    args[_i - 1] = arguments[_i];
                                }
                                path = cleanPath(state, forEachKey, path);
                                cb.apply(void 0, [new Proxy(target.get(forEachKey), validator)].concat(args));
                            });
                        };
                    case 'entries': {
                        var originalFunc_1 = target[key];
                        return function () {
                            var args = [];
                            for (var _i = 0; _i < arguments.length; _i++) {
                                args[_i] = arguments[_i];
                            }
                            var value = originalFunc_1.apply(target, args);
                            path = cleanPath(state, null, path);
                            return value;
                        };
                    }
                    case 'get': {
                        var originalFunc_2 = target[key];
                        return function (mapKey) {
                            path = cleanPath(state, mapKey, path);
                            var value = originalFunc_2.call(target, mapKey);
                            return value !== null && typeof value === 'object'
                                ? new Proxy(value, validator)
                                : value;
                        };
                    }
                    case 'delete': {
                        var originalFunction_1 = target[key];
                        return function () {
                            var args = [];
                            for (var _i = 0; _i < arguments.length; _i++) {
                                args[_i] = arguments[_i];
                            }
                            devtools.sendExecutionData({
                                type: 'mutation',
                                method: basePath.length ? 'module.unset' : 'unset',
                                args: [basePath.concat(path).concat(args[0])],
                            }, execution, functionDetails, props);
                            path = cleanPath(state, args[0], path);
                            return originalFunction_1.apply(target, args);
                        };
                    }
                    case 'clear': {
                        var originalFunction_2 = target[key];
                        return function () {
                            var args = [];
                            for (var _i = 0; _i < arguments.length; _i++) {
                                args[_i] = arguments[_i];
                            }
                            devtools.sendExecutionData({
                                type: 'mutation',
                                method: basePath.length ? 'module.set' : 'set',
                                args: [basePath.concat(path), {}],
                            }, execution, functionDetails, props);
                            path = cleanPath(state, null, path);
                            return originalFunction_2.apply(target, args);
                        };
                    }
                    case 'merge': {
                        var originalFunction_3 = target[key];
                        return function () {
                            var args = [];
                            for (var _i = 0; _i < arguments.length; _i++) {
                                args[_i] = arguments[_i];
                            }
                            devtools.sendExecutionData({
                                type: 'mutation',
                                method: basePath.length ? 'module.merge' : 'merge',
                                args: [basePath.concat(path), args[0]],
                            }, execution, functionDetails, props);
                            path = cleanPath(state, null, path);
                            return originalFunction_3.apply(target, args);
                        };
                    }
                    case 'replace':
                        var originalFunction_4 = target[key];
                        return function () {
                            var args = [];
                            for (var _i = 0; _i < arguments.length; _i++) {
                                args[_i] = arguments[_i];
                            }
                            devtools.sendExecutionData({
                                type: 'mutation',
                                method: basePath.length ? 'module.set' : 'set',
                                args: [basePath.concat(path), args[0]],
                            }, execution, functionDetails, props);
                            path = cleanPath(state, null, path);
                            return originalFunction_4.apply(target, args);
                        };
                    case 'set': {
                        var originalFunction_5 = target[key];
                        return function () {
                            var args = [];
                            for (var _i = 0; _i < arguments.length; _i++) {
                                args[_i] = arguments[_i];
                            }
                            devtools.sendExecutionData({
                                type: 'mutation',
                                method: basePath.length ? 'module.set' : 'set',
                                args: [basePath.concat(path).concat(args[0]), args[1]],
                            }, execution, functionDetails, props);
                            path = cleanPath(state, null, path);
                            return originalFunction_5.apply(target, args);
                        };
                    }
                    default:
                        return target[key];
                }
            }
            else if (target[key] instanceof ComputedClass) {
                return target[key];
            }
            else if (typeof target[key] === 'object' && target[key] !== null) {
                path = cleanPath(state, key, path);
                return new Proxy(target[key], validator);
            }
            else {
                if (typeof target[key] === 'function' &&
                    mutationMethods.indexOf(key) >= 0) {
                    var originalFunction_6 = target[key];
                    return function () {
                        var args = [];
                        for (var _i = 0; _i < arguments.length; _i++) {
                            args[_i] = arguments[_i];
                        }
                        devtools.sendExecutionData({
                            type: 'mutation',
                            method: basePath.length ? 'module.' + String(key) : key,
                            args: [basePath.concat(path)].concat(args),
                        }, execution, functionDetails, props);
                        path = cleanPath(state, key, path);
                        return originalFunction_6.apply(target, args);
                    };
                }
                return target[key];
            }
        },
        set: function (target, key, value) {
            path = cleanPath(state, key, path);
            devtools.sendExecutionData({
                type: 'mutation',
                method: basePath.length ? 'module.set' : 'set',
                args: [
                    basePath.concat(path),
                    isObservable(value) ? value.value : value,
                ],
            }, execution, functionDetails, props);
            Reflect.set(target, key, value);
            return true;
        },
    };
    return validator;
}
var legacyApi = [
    'get',
    'set',
    'toggle',
    'push',
    'merge',
    'pop',
    'shift',
    'unshift',
    'splice',
    'concat',
    'increment',
    'unset',
];
export function CreateStateProvider(state, devtools, model, useLegacyStateApi) {
    if (!devtools && useLegacyStateApi) {
        legacyApi.forEach(function (method) {
            Object.defineProperty(state, method, {
                value: function (path) {
                    var args = [];
                    for (var _i = 1; _i < arguments.length; _i++) {
                        args[_i - 1] = arguments[_i];
                    }
                    return model[method].apply(model, [path.split('.')].concat(args));
                },
            });
        });
    }
    return Provider(state, {
        wrap: devtools
            ? function (context) {
                if (useLegacyStateApi) {
                    legacyApi.forEach(function (method) {
                        Object.defineProperty(state, method, {
                            writable: true,
                            value: function (path) {
                                var args = [];
                                for (var _i = 1; _i < arguments.length; _i++) {
                                    args[_i - 1] = arguments[_i];
                                }
                                if (method !== 'get') {
                                    devtools.sendExecutionData({
                                        type: 'mutation',
                                        method: method,
                                        args: [path.split('.')].concat(args.map(function (value) {
                                            return isObservable(value) ? value.value : value;
                                        })),
                                    }, context.execution, context.functionDetails, context.props);
                                }
                                return model[method].apply(model, [path.split('.')].concat(args));
                            },
                        });
                    });
                }
                return new Proxy(state, createValidator(state, context.execution, context.functionDetails, context.props, devtools));
            }
            : false,
        ignoreDefinition: true,
    });
}
export function CreateModuleProvider(state, devtools, model, useLegacyStateApi) {
    return Provider(function (context) {
        var signalPath = context.execution.name.split('.');
        var modulePath = signalPath.splice(0, signalPath.length - 1);
        var module = modulePath.reduce(function (currentModule, key) {
            return currentModule[key];
        }, context.state);
        if (devtools && useLegacyStateApi) {
            legacyApi.forEach(function (method) {
                Object.defineProperty(module, method, {
                    writable: true,
                    value: function (path) {
                        var args = [];
                        for (var _i = 1; _i < arguments.length; _i++) {
                            args[_i - 1] = arguments[_i];
                        }
                        if (method !== 'get') {
                            devtools.sendExecutionData({
                                type: 'mutation',
                                method: 'module.' + method,
                                args: [modulePath.concat(path.split('.'))].concat(args.map(function (value) { return (isObservable(value) ? value.value : value); })),
                            }, context.execution, context.functionDetails, context.props);
                        }
                        return model[method].apply(model, [path.split('.')].concat(args));
                    },
                });
            });
        }
        if (devtools) {
            return new Proxy(module, createValidator(module, context.execution, context.functionDetails, context.props, devtools, modulePath));
        }
        if (!devtools && useLegacyStateApi) {
            legacyApi.forEach(function (method) {
                Object.defineProperty(module, method, {
                    writable: true,
                    value: function (path) {
                        var args = [];
                        for (var _i = 1; _i < arguments.length; _i++) {
                            args[_i - 1] = arguments[_i];
                        }
                        var signalPath = this.context.execution.name.split('.');
                        var modulePath = signalPath.splice(0, signalPath.length - 1);
                        return model[method].apply(model, [modulePath.concat(path.split('.'))].concat(args));
                    },
                });
            });
        }
        return module;
    });
}
//# sourceMappingURL=providers.js.map


//////////////////
// WEBPACK FOOTER
// /home/kryd0s/tmp/widgetbot/node_modules/@cerebral/fluent/es/providers.js
// module id = 71
// module chunks = 0